import { z } from 'zod';

/**
 * Misafir mobil talepleri.
 *
 * Tasarim kaynagi: Claude Design - "otelcms Misafir Mobil.dc.html".
 *
 * Misafir odadaki QR'i okutur (`<tesis>.otelcms.com/oda/104`), uygulama
 * indirmez, giris yapmaz. Oda numarasi linktedir; link yalnizca o odada
 * KONAKLAYAN biri varken calisir ve check-out'ta kendiliginden kapanir.
 *
 * Katalog burada durur cunku uc taraf da ayni seyi okur: misafirin telefonu,
 * resepsiyon ekrani ve kat hizmetleri listesi.
 *
 * DIL: misafir cogu zaman yabancidir, personel Turkce calisir. Bu yuzden
 * secenegin DB'de saklanan degeri dile bagli DEGILDIR (`kod`); ekranlar
 * `tr`/`en` etiketini koddan cozer. Boylece ayni kayit iki dilde dogru
 * gorunur ve gecmis talepler dil degisince bozulmaz.
 */

export const MISAFIR_TALEP_TIPLERI = [
  'TEMIZLIK',
  'HAVLU',
  'SERVIS',
  'GEC_CIKIS',
  'RAHATSIZ_ETMEYIN',
  'ARIZA',
] as const;
export const misafirTalepTipiSchema = z.enum(MISAFIR_TALEP_TIPLERI);
export type MisafirTalepTipi = z.infer<typeof misafirTalepTipiSchema>;

/**
 * Talep hangi ekibe duser.
 *  KAT        -> kat hizmetleri
 *  MUTFAK     -> oda servisi
 *  RESEPSIYON -> insan karari gerekir (gec cikis musaitlige bagli)
 *  TEKNIK     -> ariza
 */
export const MISAFIR_TALEP_EKIPLERI = ['KAT', 'MUTFAK', 'RESEPSIYON', 'TEKNIK'] as const;
export const misafirTalepEkibiSchema = z.enum(MISAFIR_TALEP_EKIPLERI);
export type MisafirTalepEkibi = z.infer<typeof misafirTalepEkibiSchema>;

export const EKIP_ADI: Record<MisafirTalepEkibi, { tr: string; en: string }> = {
  KAT: { tr: 'Kat hizmetleri', en: 'Housekeeping' },
  MUTFAK: { tr: 'Mutfak', en: 'Kitchen' },
  RESEPSIYON: { tr: 'Resepsiyon', en: 'Front desk' },
  TEKNIK: { tr: 'Teknik', en: 'Maintenance' },
};

/**
 * Uc adimli takip. Tasarimdaki ilerleme cubugu tam olarak bu ucludur;
 * ortadaki adimin ADI talebe gore degisir.
 */
export const MISAFIR_TALEP_DURUMLARI = ['ALINDI', 'ISLEMDE', 'TAMAMLANDI', 'IPTAL'] as const;
export const misafirTalepDurumSchema = z.enum(MISAFIR_TALEP_DURUMLARI);
export type MisafirTalepDurum = z.infer<typeof misafirTalepDurumSchema>;

/** Ilerleme cubugundaki adim sirasi; IPTAL cubukta yer almaz. */
export const TALEP_ADIMI: Record<MisafirTalepDurum, number> = {
  ALINDI: 0,
  ISLEMDE: 1,
  TAMAMLANDI: 2,
  IPTAL: -1,
};

export type TalepDili = 'tr' | 'en';

export interface MisafirTalepSecenegi {
  /** DB'de saklanan deger. Dile BAGLI DEGIL. */
  kod: string;
  tr: string;
  en: string;
  /** Folyoya islenecek ucret (kurus). Yoksa ucretsiz. */
  ucretKurus?: number;
  /** GEC_CIKIS: talep edilen saat (HH:MM) - onaylaninca rezervasyona yazilir. */
  saat?: string;
  /**
   * RAHATSIZ_ETMEYIN: kac dakika surecek. `null` = misafir kaldirana kadar.
   * Bu sure odaya islenir; kat hizmetleri listesi o odayi kilitler.
   */
  dndDk?: number | null;
}

/** Dile gore degisen metinler. */
export interface MisafirTalepMetni {
  baslik: string;
  altBaslik: string;
  aciklama: string;
  eylem: string;
  ortaAdim: string;
}

export interface MisafirTalepTanimi {
  tr: MisafirTalepMetni;
  en: MisafirTalepMetni;
  secenekler: readonly MisafirTalepSecenegi[];
  ekip: MisafirTalepEkibi;
  /** Kabaca ne kadar surer - misafire "yaklasik N dk" olarak gosterilir. */
  sureDk: number;
  /** Tasarimdaki SVG path'i (24x24 viewBox, stroke). */
  ikon: string;
}

/**
 * Katalog. Sira, misafirin telefonundaki kart sirasidir.
 * Fiyatlar kurus cinsindendir (sistemin her yerinde oldugu gibi).
 */
export const MISAFIR_TALEPLERI: Record<MisafirTalepTipi, MisafirTalepTanimi> = {
  TEMIZLIK: {
    tr: {
      baslik: 'Oda temizliği',
      altBaslik: 'Şimdi ya da saat seçin',
      aciklama: 'Kat görevlisi ortalama 20 dk içinde gelir.',
      eylem: 'Temizlik iste',
      ortaAdim: 'Yolda',
    },
    en: {
      baslik: 'Room cleaning',
      altBaslik: 'Now or pick a time',
      aciklama: 'Housekeeping arrives within about 20 minutes.',
      eylem: 'Request cleaning',
      ortaAdim: 'On the way',
    },
    secenekler: [
      { kod: 'SIMDI', tr: 'Şimdi', en: 'Now' },
      { kod: '14:00', tr: '14:00', en: '2:00 PM' },
      { kod: '16:00', tr: '16:00', en: '4:00 PM' },
    ],
    ekip: 'KAT',
    sureDk: 20,
    ikon: 'M3 21l9-9M14 4l6 6M12 12l4-4M7 17l-1 1',
  },
  HAVLU: {
    tr: {
      baslik: 'Havlu & ekstra',
      altBaslik: 'Havlu, yastık, su',
      aciklama: 'Odanıza bırakılır, kapı çalınmaz.',
      eylem: 'Getirin',
      ortaAdim: 'Yolda',
    },
    en: {
      baslik: 'Towels & extras',
      altBaslik: 'Towels, pillow, water',
      aciklama: 'Left in your room, we will not knock.',
      eylem: 'Bring it up',
      ortaAdim: 'On the way',
    },
    secenekler: [
      { kod: 'HAVLU_SETI', tr: 'Havlu seti', en: 'Towel set' },
      { kod: 'YASTIK', tr: 'Ekstra yastık', en: 'Extra pillow' },
      { kod: 'SU', tr: 'Su (2)', en: 'Water (2)' },
      { kod: 'BEBEK_YATAGI', tr: 'Bebek yatağı', en: 'Cot' },
    ],
    ekip: 'KAT',
    sureDk: 12,
    ikon: 'M4 4h16v16H4zM4 10h16M9 10v10',
  },
  SERVIS: {
    tr: {
      baslik: 'Oda servisi',
      altBaslik: 'Menü 07:00–23:00',
      aciklama: 'Ücret folyonuza işlenir, çıkışta ödersiniz.',
      eylem: 'Sipariş ver',
      ortaAdim: 'Hazırlanıyor',
    },
    en: {
      baslik: 'Room service',
      altBaslik: 'Menu 07:00–23:00',
      aciklama: 'Charged to your folio, payable at check-out.',
      eylem: 'Place order',
      ortaAdim: 'Preparing',
    },
    secenekler: [
      { kod: 'KAHVALTI', tr: 'Kahvaltı tabağı', en: 'Breakfast plate', ucretKurus: 35_000 },
      { kod: 'MEZE', tr: 'Meze tabağı', en: 'Meze platter', ucretKurus: 42_000 },
      { kod: 'KAHVE', tr: 'Türk kahvesi', en: 'Turkish coffee', ucretKurus: 9_000 },
    ],
    ekip: 'MUTFAK',
    sureDk: 25,
    ikon: 'M3 12h18M5 12a7 7 0 0114 0M12 5V3M8 20h8',
  },
  GEC_CIKIS: {
    tr: {
      baslik: 'Geç çıkış',
      altBaslik: 'Müsaitliğe göre onay',
      aciklama: 'Oda o gün boşsa ücretsiz; değilse ücret folyonuza işlenir.',
      eylem: 'Talep gönder',
      ortaAdim: 'İnceleniyor',
    },
    en: {
      baslik: 'Late check-out',
      altBaslik: 'Subject to availability',
      aciklama: 'Free if the room is empty that day, otherwise charged to your folio.',
      eylem: 'Send request',
      ortaAdim: 'Under review',
    },
    secenekler: [
      { kod: '13:00', tr: '13:00', en: '1:00 PM', saat: '13:00', ucretKurus: 20_000 },
      { kod: '14:00', tr: '14:00', en: '2:00 PM', saat: '14:00', ucretKurus: 40_000 },
      { kod: '16:00', tr: '16:00 (yarım gün)', en: '4:00 PM (half day)', saat: '16:00', ucretKurus: 70_000 },
    ],
    ekip: 'RESEPSIYON',
    sureDk: 5,
    ikon: 'M12 8v4l3 3M12 22a10 10 0 100-20 10 10 0 000 20z',
  },
  RAHATSIZ_ETMEYIN: {
    tr: {
      baslik: 'Rahatsız etmeyin',
      altBaslik: 'Kapıya asmadan',
      aciklama: 'Kat hizmetleri odanıza gelmez.',
      eylem: 'Uygula',
      ortaAdim: 'Açık',
    },
    en: {
      baslik: 'Do not disturb',
      altBaslik: 'No sign on the door',
      aciklama: 'Housekeeping will not enter your room.',
      eylem: 'Turn on',
      ortaAdim: 'Active',
    },
    secenekler: [
      { kod: 'ACIK', tr: 'Ben kaldırana kadar', en: 'Until I turn it off', dndDk: null },
      { kod: '2SAAT', tr: '2 saat', en: '2 hours', dndDk: 120 },
      { kod: 'BUGUN', tr: 'Bugün', en: 'Today', dndDk: 720 },
    ],
    ekip: 'KAT',
    sureDk: 2,
    ikon: 'M18.36 5.64A9 9 0 105.64 18.36 9 9 0 0018.36 5.64zM5.64 5.64l12.72 12.72',
  },
  ARIZA: {
    tr: {
      baslik: 'Arıza bildir',
      altBaslik: 'Klima, sıcak su, TV',
      aciklama: 'Teknik ekip önceliklendirir.',
      eylem: 'Bildir',
      ortaAdim: 'Yolda',
    },
    en: {
      baslik: 'Report a problem',
      altBaslik: 'A/C, hot water, TV',
      aciklama: 'Maintenance will prioritise it.',
      eylem: 'Report',
      ortaAdim: 'On the way',
    },
    secenekler: [
      { kod: 'KLIMA', tr: 'Klima', en: 'Air conditioning' },
      { kod: 'SICAK_SU', tr: 'Sıcak su', en: 'Hot water' },
      { kod: 'TV_WIFI', tr: 'TV / Wi-Fi', en: 'TV / Wi-Fi' },
      { kod: 'DIGER', tr: 'Diğer', en: 'Other' },
    ],
    ekip: 'TEKNIK',
    sureDk: 30,
    ikon: 'M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94z',
  },
};

/** Talebin secili dildeki metinleri. */
export function talepMetni(tip: MisafirTalepTipi, dil: TalepDili = 'tr'): MisafirTalepMetni {
  return MISAFIR_TALEPLERI[tip][dil];
}

/** Secenek kodunu tanima cevirir; bilinmeyen kod icin `null`. */
export function secenekBul(tip: MisafirTalepTipi, kod: string): MisafirTalepSecenegi | null {
  return MISAFIR_TALEPLERI[tip].secenekler.find((s) => s.kod === kod) ?? null;
}

/** Secenegin secili dildeki etiketi; fiyatli kalemlerde tutar da eklenir. */
export function secenekEtiketi(tip: MisafirTalepTipi, kod: string, dil: TalepDili = 'tr'): string {
  const s = secenekBul(tip, kod);
  // Bilinmeyen kod: eski bir kayit olabilir, ham degeri gostermek kaybolmasindan iyidir.
  if (!s) return kod;
  return s.ucretKurus ? `${s[dil]} ${paraEtiketi(s.ucretKurus)}` : s[dil];
}

/** 35_000 kurus -> "₺350". Tam sayi degilse kurusu da yazar. */
export function paraEtiketi(kurus: number): string {
  const lira = kurus / 100;
  return `₺${Number.isInteger(lira) ? lira : lira.toFixed(2)}`;
}

/**
 * Misafirin gonderdigi talep.
 * `secenek` katalogdaki KOD olmak zorunda (bkz. secenekBul).
 */
export const misafirTalepOlusturSchema = z.object({
  tip: misafirTalepTipiSchema,
  secenek: z.string().trim().min(1).max(40),
});
export type MisafirTalepOlustur = z.infer<typeof misafirTalepOlusturSchema>;

/** Konaklama bitince misafir puan verir (tasarimdaki 5 yildiz). */
export const misafirPuanSchema = z.object({ puan: z.number().int().min(1).max(5) });

/** Personel tarafi: talebi bir adim ilerletir ya da iptal eder. */
export const misafirTalepDurumGuncelleSchema = z.object({
  durum: z.enum(['ISLEMDE', 'TAMAMLANDI', 'IPTAL']),
});

/**
 * Odadaki QR'i okutan kisinin kimlik dogrulamasi.
 *
 * TCKN (11 hane) ya da pasaport numarasi kabul edilir: Turkiye'deki bir
 * otelde misafirlerin onemli bir bolumu yabancidir ve TCKN'si yoktur;
 * yalnizca TCKN istemek onlari disarida birakirdi.
 */
export const misafirKimlikSchema = z.object({
  kimlikNo: z.string().trim().min(5, 'Kimlik numarası eksik').max(20),
});
export type MisafirKimlik = z.infer<typeof misafirKimlikSchema>;

/** Tesisin misafir mobil ayarlari (yalnizca `tenant:settings` degistirir). */
export const misafirMobilAyarSchema = z.object({
  misafirMobilAcik: z.boolean().optional(),
  wifiAd: z.string().trim().max(64).nullable().optional(),
  wifiSifre: z.string().trim().max(64).nullable().optional(),
  misafirWhatsapp: z.string().trim().max(30).nullable().optional(),
});
export type MisafirMobilAyar = z.infer<typeof misafirMobilAyarSchema>;
